import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAccessToken } from '../api/client';

interface Props {
  url: string;
  name: string;
  type?: string | null;
}

export function SupportAttachment({ url, name, type }: Props) {
  const { t } = useTranslation('support');
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    async function load() {
      try {
        const token = getAccessToken();
        const headers: Record<string, string> = {};
        if (token) headers.Authorization = `Bearer ${token}`;

        const response = await fetch(url, {
          headers,
          credentials: 'same-origin',
          cache: 'no-store',
        });

        if (!response.ok) throw new Error('attachment_failed');

        const blob = await response.blob();
        if (cancelled) return;

        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        setError(false);
      } catch {
        if (!cancelled) setError(true);
      }
    }

    void load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, reloadKey]);

  if (error) {
    return (
      <small>
        {t('fileOpenFailed', { name })}{' '}
        <button type="button" className="support-attach-retry" onClick={() => setReloadKey((n) => n + 1)}>
          {t('retryShort')}
        </button>
      </small>
    );
  }
  if (!blobUrl) return <small>{t('fileLoading', { name })}</small>;

  if (type?.startsWith('image/')) {
    return (
      <a href={blobUrl} target="_blank" rel="noopener noreferrer">
        <img src={blobUrl} alt={name} style={{ maxWidth: '100%', maxHeight: 280, borderRadius: 10 }} />
      </a>
    );
  }

  if (type?.startsWith('video/')) {
    return <video src={blobUrl} controls playsInline style={{ maxWidth: '100%', maxHeight: 300 }} />;
  }

  if (type === 'application/pdf') {
    return (
      <div className="support-doc-card">
        <iframe title={name} src={blobUrl} style={{ width: '100%', height: 220, border: 0, borderRadius: 10 }} />
        <a href={blobUrl} download={name}>📄 {name}</a>
      </div>
    );
  }

  return (
    <a className="support-doc-link" href={blobUrl} download={name}>
      📎 {name}
    </a>
  );
}
